import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../services/apiService';
import logger from '../utils/logger';

/**
 * useListingDetail - Hook for lazy-loading full listing details via feed_entry_id.
 *
 * Does not fetch until `enabled` is true (i.e., when the popup opens).
 * This keeps the initial page load lightweight and only fetches detailed
 * listing data (features, specs, seller comments) on demand.
 *
 * @param {string|null} feedEntryId - The user_listing_feeds UUID to fetch details for
 * @param {boolean} [enabled=false] - Whether to fetch (set to true when popup opens)
 * @returns {{ detail: Object|null, isLoading: boolean, error: string|null, refetch: Function }}
 */
export const useListingDetail = (feedEntryId, enabled = false) => {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const previousIdRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    if (!feedEntryId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getListingDetailByFeedEntry(feedEntryId);
      // Merge the model AI image URL onto the detail so the popup gallery can fall back to it
      // when a dealer photo fails to load (broken-photo-model-fallback spec).
      setDetail(
        response.listing
          ? { ...response.listing, modelImageUrl: response.modelImageUrl ?? null }
          : response
      );
    } catch (err) {
      const errorMessage =
        err.message || 'Failed to load listing details. Please try again.';
      logger.error('[useListingDetail] Error fetching detail:', err);
      Sentry.captureException(err, {
        tags: { hook: 'useListingDetail', operation: 'fetchDetail' },
        contexts: { listing: { feed_entry_id: feedEntryId } },
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [feedEntryId]);

  useEffect(() => {
    if (feedEntryId !== previousIdRef.current) {
      setDetail(null);
      setError(null);
      setIsLoading(false);
      previousIdRef.current = feedEntryId;
    }

    if (enabled && feedEntryId) {
      fetchDetail();
    }
  }, [feedEntryId, enabled, fetchDetail]);

  return {
    detail,
    isLoading,
    error,
    refetch: fetchDetail,
  };
};
